import { memo, useCallback } from 'react'

import { AutoDeleteOutlined, ControlPointDuplicate } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Skeleton, Typography, Divider } from '@mui/material'
import { AudioStream } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useAudioStreamCreateMutation, useAudioStreamDeleteMutation } from 'hooks/mutations/content'

const RowActionsRaw = ({ row, onClose, onClick }: RowActionsProps<AudioStream>) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createAudioStream, ...audioStreamCreateMutation } = useAudioStreamCreateMutation()
  const { mutate: deleteAudioStream } = useAudioStreamDeleteMutation()

  const handleDuplicateAudioStream = useCallback(() => {
    createAudioStream(
      { audioStreamInsert: row.original },
      {
        onSuccess: () => {
          onClose()
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [createAudioStream, enqueueSnackbar, onClose, row.original, t])

  const handleDeleteAudioStream = useCallback(() => {
    onClose()

    deleteAudioStream(
      { streamId: row.original.id },
      {
        onSuccess: () =>
          enqueueSnackbar(t('domain:Workspace.Audio.nameDelete', { name: row.original.name }), { variant: 'success' }),
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteAudioStream, enqueueSnackbar, onClose, row.original.id, row.original.name, t])

  const handleLifecycleAudioStream = useCallback(() => {
    onClose()
    onClick?.(row)
  }, [onClick, onClose, row])

  return (
    <>
      <MenuItem onClick={handleDuplicateAudioStream} disabled={audioStreamCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {audioStreamCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItem onClick={handleLifecycleAudioStream}>
        <ListItemIcon>
          <AutoDeleteOutlined />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {t('domain:Workspace.Audio.setContentLifecyclePolicy', { count: 1 })}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeleteAudioStream} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
